import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BellIcon, CheckIcon, XIcon } from 'lucide-react';
import { alertChannels, alertTypes, thresholds } from '../../data/eau';
interface AlertPreferencesProps {
  onClose: () => void;
}
export function AlertPreferences({
  onClose
}: AlertPreferencesProps) {
  const [threshold, setThreshold] = useState(thresholds[1]);
  const [activeChannels, setActiveChannels] = useState<string[]>(['courriel', 'push']);
  const [activeTypes, setActiveTypes] = useState<string[]>(['fuite', 'facture']);
  const [saved, setSaved] = useState(false);
  const toggle = <T,>(list: T[], value: T): T[] => list.includes(value) ? list.filter(item => item !== value) : [...list, value];
  const save = () => {
    setSaved(true);
    window.setTimeout(onClose, 900);
  };
  return <motion.section aria-labelledby="alertes-titre" initial={{
    opacity: 0,
    y: -8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="rounded-3xl border border-slate-200 bg-white p-6" data-id="element-1130" data-mp="1by1w0i14e7o1">
      <div className="flex items-start justify-between gap-4" data-id="element-1131" data-mp="2rcgykfv9d34w">
        <div data-id="element-1132" data-mp="2wb0lojll8kyu">
          <h3 id="alertes-titre" className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-1133" data-mp="2waisxwy4wovo">
            <BellIcon className="h-5 w-5 text-ville-blue" aria-hidden="true" data-id="element-1134" data-mp="30a13pzw8rjks" />
            Alertes de consommation
          </h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-1135" data-mp="3aa09xq56uxuh">Choisissez ce qui doit vous être signalé, et comment.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Fermer les préférences d’alerte" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1136" data-mp="3f2l2koccz4wf">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1137" data-mp="1sy0du9nekbj2" />
        </button>
      </div>

      <fieldset className="mt-5" data-id="element-1138" data-mp="1oxgd6w3meps6">
        <legend className="text-sm font-bold text-ville-navy" data-id="element-1139" data-mp="1oyhql783zod2">Quand faut-il vous avertir ?</legend>
        <div className="mt-3 space-y-2.5" data-id="element-1140" data-mp="22pd44wtjytmz">
          {alertTypes.map(type => <label key={type.id} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3 transition-colors duration-150 ease-ville hover:border-ville-indigo" data-id="element-1141" data-mp="1xwts44aik6ka">
              <input type="checkbox" checked={activeTypes.includes(type.id)} onChange={() => setActiveTypes(current => toggle(current, type.id))} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-ville-blue focus:ring-ville-indigo" data-id="element-1142" data-mp="27psh3n9x9z4f" />
              <span className="min-w-0" data-id="element-1143" data-mp="macygqwdgf1y">
                <span className="block text-sm font-bold text-ville-navy" data-id="element-1144" data-mp="mbektriu8nyz">{type.label}</span>
                <span className="block text-[13px] text-slate-500" data-id="element-1145" data-mp="2xxf56abnee0u">{type.hint}</span>
              </span>
            </label>)}
        </div>
      </fieldset>

      <div className="mt-6" data-id="element-1146" data-mp="2p5d3y9tr8tpi">
        <label htmlFor="alerte-seuil" className="text-sm font-bold text-ville-navy" data-id="element-1147" data-mp="2p66e7krxa1ce">
          Seuil mensuel
        </label>
        <select id="alerte-seuil" value={threshold} onChange={event => setThreshold(event.target.value)} className="mt-2 w-full max-w-xs rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1148" data-mp="2t5f76nf51v6w">
          {thresholds.map(option => <option key={option} value={option} data-id="element-1149" data-mp="4ew5lkcqb0mw">
              {option}
            </option>)}
        </select>
      </div>

      <fieldset className="mt-6" data-id="element-1150" data-mp="8g7pd98x8rn6">
        <legend className="text-sm font-bold text-ville-navy" data-id="element-1151" data-mp="3fvhcs6fuz22k">Comment vous joindre ?</legend>
        <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3" data-id="element-1152" data-mp="3ft4yc39b79u9">
          {alertChannels.map(channel => <label key={channel.id} className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ville-navy" data-id="element-1153" data-mp="35xbc1s5sg1za">
              <input type="checkbox" checked={activeChannels.includes(channel.id)} onChange={() => setActiveChannels(current => toggle(current, channel.id))} className="h-4 w-4 rounded border-slate-300 text-ville-blue focus:ring-ville-indigo" data-id="element-1154" data-mp="30xyof573eqvn" />
              {channel.label}
            </label>)}
        </div>
      </fieldset>

      <div className="mt-6 flex items-center justify-end gap-3" data-id="element-1155" data-mp="2i5w7rtugbale">
        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1156" data-mp="2d84gn21mrsi9">
          Annuler
        </button>
        <button type="button" onClick={save} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1157" data-mp="ssg3h4ndo9wo">
          {saved && <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1158" data-mp="a0ndzk7ip6tp" />}
          {saved ? 'Alertes enregistrées' : 'Enregistrer mes alertes'}
        </button>
      </div>
      <p className="sr-only" role="status" data-id="element-1159" data-mp="jq6uij7rcole">
        {saved ? 'Vos alertes sont enregistrées.' : ''}
      </p>
    </motion.section>;
}